import Refresh from "@mui/icons-material/Refresh"
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import moment from "moment"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Feature } from "shared/feature"
import { FeatureAssignment } from "shared/featureAssignment"
import { FeatureAssignmentThingTypeMrn } from "shared/featureAssignmentData"
import {
  FeatureAccessState,
  isFeatureAccessGranted,
  readDefaultActivated,
  resolveFeatureAccessState,
} from "shared/featureResolution"
import {
  useGetFeatureAssignmentsByTenantIdQuery,
  useGetFeaturesQuery,
  useSaCreateThingsMutation,
  useSaUpdateThingsMutation,
} from "src/features/things/thingsSaApi"
import SaTenantFeatureToggleDialog, {
  SaTenantPendingToggle,
} from "./SaTenantFeatureToggleDialog"

type SaTenantFeaturesProps = {
  tenantId: string
  limitFeatures?: number
  limitAssignments?: number
}

// Component showing list of features with activation checkbox for a given tenant
export const SaTenantFeatures: FC<SaTenantFeaturesProps> = ({
  tenantId,
  limitFeatures = 1000,
  limitAssignments = 3000,
}) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")

  const featuresQuery = useGetFeaturesQuery({
    params: { limit: limitFeatures },
  })
  const assignmentsQuery = useGetFeatureAssignmentsByTenantIdQuery({
    tenantId,
    params: { limit: limitAssignments },
  })

  const [createThings, { isLoading: isCreating }] = useSaCreateThingsMutation()
  const [updateThings, { isLoading: isUpdating }] = useSaUpdateThingsMutation()

  const assignmentsByFeatureId = useMemo(() => {
    const map: Record<string, FeatureAssignment> = {}
    assignmentsQuery.data?.things?.forEach((a: any) => {
      if (a.featureId) map[a.featureId] = a
    })
    return map
  }, [assignmentsQuery.data])

  const isLoading = featuresQuery.isLoading || assignmentsQuery.isLoading
  const hasError = featuresQuery.error || assignmentsQuery.error

  const filteredFeatures = useMemo(() => {
    const list =
      featuresQuery.data?.things?.slice().sort((a: any, b: any) => {
        return (a.name || a.id || "").localeCompare(b.name || b.id || "")
      }) || []
    const s = search.trim().toLowerCase()
    if (!s) return list
    return list.filter((f: Feature) => {
      return (
        (f.name || "").toLowerCase().includes(s) ||
        (f.category || "").toLowerCase().includes(s) ||
        (f.description || "").toLowerCase().includes(s) ||
        (f.id || "").toLowerCase().includes(s)
      )
    })
  }, [featuresQuery.data, search])

  const [pendingToggle, setPendingToggle] =
    useState<SaTenantPendingToggle | null>(null)
  const [toggleError, setToggleError] = useState<string | undefined>()

  /**
   * `willActivate` is the opposite of the row's RESOLVED state, not of its
   * assignment.
   *
   * Since LOCKER-408 a row can read "on" with no assignment at all (granted by
   * the feature default). Deriving the direction from
   * `!(assignment?.isActivated || false)` would have written ON for a row
   * already showing on — the operator clicks to turn something off and it stays
   * on, which is the same class of silent no-op this ticket exists to remove.
   */
  const performToggle = useCallback(
    async (
      feature: Feature,
      currentAssignment: FeatureAssignment | undefined,
      isActivated: boolean,
    ) => {
      const nowIso = new Date().toISOString()
      const willActivate = !isActivated
      setToggleError(undefined)
      try {
        if (!currentAssignment) {
          await createThings({
            body: {
              things: [
                {
                  thingTypeMrn: FeatureAssignmentThingTypeMrn,
                  tenantId,
                  featureId: feature.id,
                  featureRefName: feature.refName,
                  isActivated: willActivate,
                  activatedAt: willActivate ? nowIso : undefined,
                },
              ],
            },
            params: { tenantId },
          }).unwrap()
        } else {
          await updateThings({
            body: {
              things: [
                {
                  id: currentAssignment.id,
                  thingTypeMrn: currentAssignment.thingTypeMrn,
                  tenantId: currentAssignment.tenantId,
                  isActivated: willActivate,
                  activatedAt: willActivate ? nowIso : undefined,
                },
              ],
            },
            params: { tenantId },
          }).unwrap()
        }
        assignmentsQuery.refetch()
        return true
      } catch (e: any) {
        // Surfaced in the UI. This was a bare console.error, so a rejected
        // write left the row unchanged with nothing on screen saying why
        // (LOCKER-408 DoD6).
        setToggleError(
          e?.data?.message ||
            e?.data?.error ||
            e?.message ||
            t("feature.saveFailed"),
        )
        return false
      }
    },
    [createThings, updateThings, tenantId, assignmentsQuery, t],
  )

  return (
    <Card data-ui="UI-SL2W-135" variant="outlined" sx={{ maxWidth: 820 }}>
      <CardContent>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              {t("features")}
            </Typography>
            <TextField
              size="small"
              placeholder={t("search") || "Search"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 250 }}
            />
            <IconButton
              onClick={() => {
                featuresQuery.refetch()
                assignmentsQuery.refetch()
              }}
            >
              <Refresh />
            </IconButton>
            {(isCreating || isUpdating) && <CircularProgress size={18} />}
          </Stack>
          <Divider />
          {isLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2">{t("processing")}</Typography>
            </Stack>
          )}
          {hasError && (
            <Typography variant="body2" color="error">
              {(featuresQuery.error as any)?.message ||
                (assignmentsQuery.error as any)?.message ||
                "Error"}
            </Typography>
          )}
          {toggleError && (
            <Alert
              severity="error"
              onClose={() => setToggleError(undefined)}
              data-testid="sa-tenant-feature-toggle-error"
            >
              {toggleError}
            </Alert>
          )}
          {!isLoading && filteredFeatures.length === 0 && (
            <Box
              sx={{
                height: 400,
                minHeight: 400,
                maxHeight: 400,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("noData")}
              </Typography>
            </Box>
          )}
          {!isLoading && filteredFeatures.length > 0 && (
            <Stack
              direction="column"
              spacing={1}
              sx={{
                height: 400,
                minHeight: 400,
                maxHeight: 400,
                overflowY: "scroll",
              }}
            >
              {filteredFeatures.map((f: any) => {
                const assignment = assignmentsByFeatureId[f.id]
                // Resolved through the same module as `/sa/features/:id` and
                // the backend, so this mirror view cannot report a different
                // effective state for the same feature+tenant pair
                // (LOCKER-408 DoD7). It previously read the assignment alone,
                // so a feature granted by `defaultActivated` showed as off.
                const state = resolveFeatureAccessState(
                  assignment,
                  readDefaultActivated(f),
                )
                const isActivated = isFeatureAccessGranted(state)
                const viaDefault =
                  state === FeatureAccessState.ActivatedByDefault
                return (
                  <Stack
                    key={f.id}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      border: "1px solid",
                      borderColor: "grey.200",
                      p: 1,
                      borderRadius: 1,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{ flex: 1 }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {f.name || f.id}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {f.category || "-"}
                        </Typography>
                      </Box>
                      {readDefaultActivated(f) && (
                        <Tooltip
                          title={
                            t("feature.defaultActivated") || "Default activated"
                          }
                        >
                          <Typography variant="caption" color="success.main">
                            {t("default") || "default"}
                          </Typography>
                        </Tooltip>
                      )}
                    </Stack>
                    <Stack direction="column" spacing={1} alignItems="flex-end">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          data-testid={`sa-tenant-feature-state-${f.id}`}
                        >
                          {viaDefault
                            ? t("feature.stateOnViaDefault")
                            : isActivated
                              ? t("activated")
                              : t("deactivated")}
                        </Typography>
                        <Checkbox
                          size="small"
                          checked={isActivated}
                          onChange={() =>
                            setPendingToggle({
                              feature: f,
                              assignment,
                              isActivated,
                            })
                          }
                          inputProps={
                            {
                              "aria-label": t("feature.toggleOrganization", {
                                name: f.name || f.id,
                              }),
                              "data-testid": `sa-tenant-feature-toggle-${f.id}`,
                            } as any
                          }
                        />
                      </Stack>
                      {/* moment updatedAt format YYYY-MM-DD HH:mm:ss */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", lineHeight: 1.2 }}
                      >
                        {assignment &&
                          moment(
                            assignment.updatedAt || assignment.createdAt,
                          ).format("YYYY-MM-DD HH:mm:ss")}
                      </Typography>
                    </Stack>
                  </Stack>
                )
              })}
            </Stack>
          )}
        </Stack>
      </CardContent>
      <SaTenantFeatureToggleDialog
        pending={pendingToggle}
        isBusy={isCreating || isUpdating}
        onCancel={() => setPendingToggle(null)}
        onConfirm={async (p) => {
          await performToggle(p.feature, p.assignment, p.isActivated)
          setPendingToggle(null)
        }}
      />
    </Card>
  )
}

export default SaTenantFeatures
